import {EventEmitter, Subscription} from "nate-react-api-helpers";
import {inches} from "../const";
import {ImperialDistance} from "../../../../../misc/ImperialDistance";

const DEBUG = false;

export class FrmVar {
    private _debugName: string = "";
    private _value: number;
    private _defaultAsModified: boolean = false;
    private _isModified: boolean = false;
    private _isCalculated: boolean = false;
    // populated by createCalculated(): the human-readable formula string and the
    // named dependencies it derives from. Retained solely so the debug panel can
    // render "name = formula" — the calculate() closure itself is not introspectable.
    private _formula: string | undefined;
    private _deps: Record<string, FrmVar> | undefined;
    private _emitter = new EventEmitter<FrmVarEvent>();

    // fires on every user-initiated edit (drag / dimension commit / unlock) across all FrmVars.
    // Lets a consumer flag "unsaved changes" without subscribing to each var. Calculated
    // recomputes, load-time setInitial and programmatic set() deliberately don't fire it.
    static anyUserSet = new EventEmitter<FrmVar>();

    constructor(name: string, value: number) {
        this._value = value;
        this._debugName = name;

        if(DEBUG) {
            this._emitter.subscribe((ev) => {
                console.log(this._debugName, ev.debug, ev.shapeUpdateId, this.getDebug());
            })
        }
    }

    get() {
        return this._value;
    }

    userSet(value: number, ev: FrmVarEvent) {
        if(value !== this._value) {
            this._isModified = true;
        }

        this._value = value;
        this.emit(ev);
        FrmVar.anyUserSet.emit(this);
    }

    set(value: number, ev: FrmVarEvent) {
        this._value = value;
        this.emit(ev);
    }

    isModified(): boolean {
        return this._isModified;
    }

    // true once createCalculated() has wired this var to derive its value from
    // other vars. calculated vars are read-only from the user's perspective —
    // editing them would just be overwritten on the next dependency change.
    isCalculated(): boolean {
        return this._isCalculated;
    }

    overrideIsModified(b: boolean) {
        this._defaultAsModified = b;
        this._isModified = true;
        return this;
    }

    createCalculated<T extends Record<string, FrmVar>>(
        calculate: (props: { [K in keyof T]: number }) => number,
        deps: T,
        // optional human-readable form of `calculate`, e.g.
        // "jambDepth - (topTab + bottomTab)". Displayed verbatim in the debug
        // panel; not used in the computation.
        formula?: string,
    ) {
        this._isCalculated = true;
        this._formula = formula;
        this._deps = deps;

        const recalc = (ev: FrmVarEvent) => {
            const props = {} as { [K in keyof T]: number };
            for(const key in deps) {
                props[key] = deps[key].get();
            }

            const next = calculate(props);
            if(next !== this._value) {
                this._value = next;
                this.emit(ev);
            }
        };

        for(const key in deps) {
            deps[key]._emitter.subscribe(recalc);
        }

        recalc({debug: `${this._debugName}.calculated`});

        return this;
    }

    // set the value as if freshly loaded, clearing the modified flag (unless the var
    // was flagged default-as-modified). Silent by default (used during construction);
    // pass emit=true for a live reset so calculated dependents re-derive and current
    // subscribers redraw.
    setInitial(value: number, emit: boolean = false) {
        this._value = value;

        if(!this._defaultAsModified) {
            this._isModified = false;
        }

        if(emit) this.emit({debug: "setInitial"});
    }

    restore(value: number, modified: boolean) {
        this._value = value;
        this._isModified = modified;
        this.emit({debug: "restore"});
    }

    subscribe(param: (ev: FrmVarEvent) => void): Subscription {
        return this._emitter.subscribe(param);
    }

    clearModified() {
        this._isModified = false;
        this.emit({debug: "clearModified"});
    }

    // clearModified from a user action (e.g. unlocking a dimension), so it counts as an edit
    userClearModified() {
        this.clearModified();
        FrmVar.anyUserSet.emit(this);
    }

    // lock the var at its current value (mark it user-modified without changing the value —
    // userSet only flags a modification when the value actually changes).
    markModified() {
        this._isModified = true;
        this.emit({debug: "markModified"});
    }

    private emit(input: FrmVarEvent) {
        this._emitter.emit(input);
    }

    getDebug() {
        const val = ImperialDistance.fromFeetAndInches({inches: this._value/inches}).toString();
        return `${this._debugName}=${val},${this._isModified}`;
    }

    // the dotted code path this var was constructed with (e.g. "frameSpec.jambDepth").
    // Not guaranteed unique — a few vars share a name — so callers should key by
    // field path, not this.
    name() {
        return this._debugName;
    }

    // repoint this var's debug label at its new role. Used when a var is repurposed
    // rather than recreated (e.g. a leaf's `:size` var reused as the wrapping block's
    // size on a split) so the debug panel names it for what it now drives instead of
    // where it came from. Debug-only — no effect on value/modified state.
    rename(name: string) {
        this._debugName = name;
        return this;
    }

    // structured snapshot for the debug panel. `deps`/`formula` are only present on
    // calculated vars (see createCalculated).
    getFormulaDebug(): FrmVarDebug {
        return {
            name: this._debugName,
            value: this._value,
            isModified: this._isModified,
            isCalculated: this._isCalculated,
            formula: this._formula,
            deps: this._deps
                ? Object.entries(this._deps).map(([prop, v]) => ({prop, name: v._debugName, value: v._value}))
                : undefined,
        };
    }

    subscribeAndFireOnce(param: () => void) {
        const sub = this.subscribe(param);
        param();
        return sub;
    }
}

export type FrmVarEvent = {
    debug: string;
    shapeUpdateId?: number;
}

// structured snapshot of a FrmVar for the debug panel. Values are in internal
// drawing units (divide by `inches` to display).
export type FrmVarDebug = {
    name: string;
    value: number;
    isModified: boolean;
    isCalculated: boolean;
    formula?: string;
    deps?: {prop: string; name: string; value: number}[];
}